import { PopupCategory, PopupItem } from '../constants/types/popup-type';
import instance from '../api/axios/utils.ts/instance';
import { ApiResponse } from '../constants/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  hidePopupUntilEndOfDay,
  hidePopupForDays,
  shouldShowPopup,
} from '../util/popupStorage';
import axios from 'axios';

const isWithinDisplayPeriod = (item: PopupItem, now: number) => {
  const startOk = !item.fromDate || new Date(item.fromDate).getTime() <= now;
  const endOk = !item.toDate || new Date(item.toDate).getTime() >= now;

  return startOk && endOk;
};

const getDisplayablePopups = (items: PopupItem[]): PopupItem[] => {
  const now = Date.now();

  return items
    .filter((item) => item.isActive)
    .filter((item) => isWithinDisplayPeriod(item, now))
    .filter((item) => shouldShowPopup(item.id))
    .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
};

const fetchPopups = async (categoryId: PopupCategory) => {
  const response = await instance.get<ApiResponse<PopupItem[]>>(
    '/v1/public/popups',
    {
      params: {
        categoryId: categoryId,
      },
    },
  );

  return response.data.data;
};

const increasePopupView = async (popupId: number): Promise<void> => {
  await instance.post(`/v1/public/popups/${popupId}/view`);
};

const increasePopupClick = async (popupId: number): Promise<void> => {
  await instance.post(`/v1/public/popups/${popupId}/click`);
};

type UsePopupResult = {
  popups: PopupItem[];
  currentPopup: PopupItem | null;
  currentIndex: number;
  open: boolean;
  loading: boolean;
  error: string | null;
  closeCurrent: () => void;
  closeCurrentForToday: () => void;
  closeCurrentForDays: (days: number) => void;
  goNext: () => void;
  clickCurrent: () => Promise<void>;
  refetch: () => Promise<void>;
};

export const usePopups = (categoryId: PopupCategory): UsePopupResult => {
  const [rawPopups, setRawPopups] = useState<PopupItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const prevOpenRef = useRef(false);
  const prevPopupIdRef = useRef<number | null>(null);

  const popups = useMemo(() => {
    return getDisplayablePopups(rawPopups);
  }, [rawPopups]);

  const currentPopup = popups[currentIndex] ?? null;

  const loadPopups = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await fetchPopups(categoryId);

      setRawPopups(data);
      setCurrentIndex(0);
      setOpen(data.length > 0);
    } catch (err) {
      setRawPopups([]);
      setCurrentIndex(0);
      setOpen(false);

      if (axios.isAxiosError(err)) {
        setError(err.message || '팝업 정보를 불러오지 못했습니다.');
      } else {
        setError('팝업 정보를 불러오지 못했습니다.');
      }
    } finally {
      setLoading(false);
    }
  }, [categoryId]);

  useEffect(() => {
    void loadPopups();
  }, [loadPopups]);

  useEffect(() => {
    setOpen(!!currentPopup);
  }, [currentPopup]);

  useEffect(() => {
    // 현재 노출하려는 팝업 ID (없으면 null)
    const popupId = currentPopup?.id ?? null;

    // 1. 모달이 닫힘 -> 열림으로 상태가 바뀐 경우
    const openedNow = !prevOpenRef.current && open;

    // 2. 모달이 열린 상태에서 다른 팝업으로 교체된 경우
    // (여러 팝업을 순차적으로 보여줄 때 발생)
    const popupChangedWhileOpen =
      prevOpenRef.current &&
      open &&
      popupId !== null &&
      popupId !== prevPopupIdRef.current;

    if ((openedNow || popupChangedWhileOpen) && popupId !== null) {
      void increasePopupView(popupId).catch((err) => {
        console.error('팝업 노출 수 집계 실패', err);
      });
    }

    // 다음 렌더링 비교를 위해 이전 상태 저장
    prevOpenRef.current = open;
    prevPopupIdRef.current = popupId;
  }, [open, currentPopup]);

  const closeCurrent = useCallback(() => {
    const isLast = currentIndex >= popups.length - 1;

    if (isLast) {
      setOpen(false);
      return;
    }

    setCurrentIndex((prev) => prev + 1);
  }, [currentIndex, popups.length]);

  const closeCurrentForToday = useCallback(() => {
    if (!currentPopup) return;

    hidePopupUntilEndOfDay(currentPopup.id);

    const isLast = currentIndex >= popups.length - 1;

    if (isLast) {
      setOpen(false);
      return;
    }

    setCurrentIndex((prev) => prev + 1);
  }, [currentIndex, currentPopup, popups.length]);

  const closeCurrentForDays = useCallback(
    (days: number) => {
      if (!currentPopup) return;

      hidePopupForDays(currentPopup.id, days);

      const isLast = currentIndex >= popups.length - 1;

      if (isLast) {
        setOpen(false);
        return;
      }

      setCurrentIndex((prev) => prev + 1);
    },
    [currentIndex, currentPopup, popups.length],
  );

  const goNext = useCallback(() => {
    const isLast = currentIndex >= popups.length - 1;

    if (isLast) {
      setOpen(false);
      return;
    }

    setCurrentIndex((prev) => prev + 1);
  }, [currentIndex, popups.length]);

  const clickCurrent = useCallback(async (): Promise<void> => {
    if (!currentPopup?.linkUrl) return;

    try {
      await increasePopupClick(currentPopup.id);
    } catch (err) {
      console.error('팝업 클릭 수 집계 실패', err);
    }
  }, [currentPopup]);

  return {
    popups,
    currentPopup,
    currentIndex,
    open,
    loading,
    error,
    closeCurrent,
    closeCurrentForToday,
    closeCurrentForDays,
    goNext,
    clickCurrent,
    refetch: loadPopups,
  };
};
